/**
 * Supabase Subscription Repository
 * Handles SMS opt-in/opt-out via Supabase
 */

import type {
  ISubscriptionRepository,
  SMSSubscription,
} from '../interfaces/ISubscriptionRepository'
import { requireSupabaseClient } from '@/lib/supabase'

export class SupabaseSubscriptionRepository implements ISubscriptionRepository {
  async subscribeToSMS(phoneNumber: string): Promise<void> {
    const supabase = requireSupabaseClient()

    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser()

    if (authError || !user) {
      throw new Error('Not authenticated. Please log in again.')
    }

    const { error } = await supabase.from('sms_subscribers').upsert(
      {
        user_id: user.id,
        phone_number: phoneNumber,
        is_active: true,
        consented_at: new Date().toISOString(),
        consent_source: 'web_opt_in_form',
        opted_out_at: null,
      },
      { onConflict: 'user_id' }
    )

    if (error) {
      throw new Error(`Failed to subscribe to SMS: ${error.message}`)
    }
  }

  async unsubscribeFromSMS(): Promise<void> {
    const supabase = requireSupabaseClient()

    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser()

    if (authError || !user) {
      throw new Error('Not authenticated. Please log in again.')
    }

    const { error } = await supabase
      .from('sms_subscribers')
      .update({
        is_active: false,
        opted_out_at: new Date().toISOString(),
      })
      .eq('user_id', user.id)

    if (error) {
      throw new Error(`Failed to unsubscribe from SMS: ${error.message}`)
    }
  }

  async getSMSSubscription(): Promise<SMSSubscription | null> {
    const supabase = requireSupabaseClient()

    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser()

    if (authError || !user) return null

    const { data, error } = await supabase
      .from('sms_subscribers')
      .select('phone_number, is_active, consented_at')
      .eq('user_id', user.id)
      .single<{ phone_number: string; is_active: boolean; consented_at: string }>()

    if (error || !data) return null

    return {
      phoneNumber: data.phone_number,
      isActive: data.is_active,
      consentedAt: new Date(data.consented_at),
    }
  }
}
